import { sectorsFor, type Coord, type Sector } from '../data/communications'
import type { Localized } from '../types/notice'
import type { City } from './activeCity'
import { BROSSARD_DEMO_REQUESTS } from '../data/brossardRequests'

export type PinIcon = 'light' | 'road' | 'bin' | 'tree' | 'water' | 'sign'
export type SlaState = 'ok' | 'risk' | 'late'

/** Workflow status of the request (open requests only on this map). */
export type RequestStatus = 'nouvelle' | 'assignee' | 'en_cours' | 'en_attente'

export const REQUEST_STATUS_LABELS: Record<RequestStatus, Localized> = {
  nouvelle: { fr: 'Nouvelle', en: 'New' },
  assignee: { fr: 'Assignée', en: 'Assigned' },
  en_cours: { fr: 'En cours', en: 'In progress' },
  en_attente: { fr: 'En attente', en: 'On hold' },
}

function statusFor(state: SlaState, days: number, seed: number): RequestStatus {
  if (days <= 1) return 'nouvelle'
  if (state === 'late') return seed % 2 === 0 ? 'en_attente' : 'en_cours'
  return seed % 3 === 0 ? 'assignee' : 'en_cours'
}

export interface RequestPin {
  id: string
  number: number
  sectorId: string
  position: Coord
  icon: PinIcon
  category: Localized
  subcategory: Localized
  address: string
  days: number
  slaDays: number
  state: SlaState
  summary: Localized
  actions: Localized[]
  explanation: Localized
  status: RequestStatus
}

interface PinTemplate {
  icon: PinIcon
  category: Localized
  subcategory: Localized
  slaDays: number
  summary: Localized
  actions: Localized[]
  lateReasons: Localized[]
}

const TEMPLATES: PinTemplate[] = [
  {
    icon: 'light',
    category: { fr: 'Éclairage public', en: 'Street lighting' },
    subcategory: { fr: 'Lampadaire défectueux', en: 'Broken streetlight' },
    slaDays: 14,
    summary: {
      fr: 'Un citoyen signale un lampadaire éteint depuis plusieurs soirs, rendant le trottoir peu sûr à la tombée de la nuit.',
      en: 'A citizen reports a streetlight that has been out for several nights, making the sidewalk unsafe after dark.',
    },
    actions: [
      { fr: 'Requête assignée aux Travaux publics', en: 'Request assigned to Public Works' },
      { fr: 'Diagnostic sur place : ballast à remplacer', en: 'On-site diagnosis: ballast to replace' },
      { fr: 'Pièce commandée chez le fournisseur', en: 'Part ordered from the supplier' },
    ],
    lateReasons: [
      {
        fr: 'La pièce de remplacement est en rupture chez le fournisseur : l’intervention attend la livraison, prévue dans 4 jours.',
        en: 'The replacement part is out of stock at the supplier: the repair is waiting on delivery, expected in 4 days.',
      },
      {
        fr: 'La nacelle de l’équipe d’éclairage est en réparation depuis une semaine, ce qui a repoussé toutes les interventions en hauteur.',
        en: 'The lighting crew’s bucket truck has been under repair for a week, pushing back all work at height.',
      },
    ],
  },
  {
    icon: 'road',
    category: { fr: 'Voirie', en: 'Roads' },
    subcategory: { fr: 'Nid-de-poule', en: 'Pothole' },
    slaDays: 7,
    summary: {
      fr: 'Nid-de-poule profond signalé dans la voie de droite, avec deux signalements supplémentaires au même endroit.',
      en: 'Deep pothole reported in the right lane, with two additional reports at the same spot.',
    },
    actions: [
      { fr: 'Requêtes en double fusionnées', en: 'Duplicate requests merged' },
      { fr: 'Balisage temporaire installé', en: 'Temporary markers installed' },
      { fr: 'Colmatage planifié avec l’équipe d’asphalte', en: 'Patching scheduled with the asphalt crew' },
    ],
    lateReasons: [
      {
        fr: 'L’asphalte chaud n’est disponible qu’à partir de la mi-avril : seul un colmatage à froid temporaire a pu être fait, la réparation définitive reste en attente.',
        en: 'Hot asphalt is only available from mid-April: only a temporary cold patch was possible, the permanent repair is pending.',
      },
      {
        fr: 'La requête a été réassignée deux fois entre Voirie et Génie avant d’être prise en charge, ce qui a fait perdre 5 jours.',
        en: 'The request was reassigned twice between Roads and Engineering before being picked up, losing 5 days.',
      },
    ],
  },
  {
    icon: 'bin',
    category: { fr: 'Matières résiduelles', en: 'Waste management' },
    subcategory: { fr: 'Réparation d’un bac brisé', en: 'Broken bin repair' },
    slaDays: 14,
    summary: {
      fr: 'Le couvercle du bac de recyclage s’est détaché lors de la dernière collecte. Le citoyen demande une réparation ou un remplacement.',
      en: 'The recycling bin lid came off during the last pickup. The citizen asks for a repair or replacement.',
    },
    actions: [
      { fr: 'Photo du bac reçue du citoyen', en: 'Photo of the bin received from the citizen' },
      { fr: 'Demande transmise au fournisseur de bacs', en: 'Request sent to the bin supplier' },
      { fr: 'Livraison d’un bac de remplacement planifiée', en: 'Replacement bin delivery scheduled' },
    ],
    lateReasons: [
      {
        fr: 'Le fournisseur de bacs regroupe ses livraisons par tournée hebdomadaire : ce secteur n’est desservi que le vendredi.',
        en: 'The bin supplier groups deliveries into a weekly route: this sector is only served on Fridays.',
      },
      {
        fr: 'Le numéro de série du bac ne correspondait pas à l’adresse, une vérification au dossier a été nécessaire avant le remplacement.',
        en: 'The bin’s serial number didn’t match the address, so a records check was needed before replacing it.',
      },
    ],
  },
  {
    icon: 'tree',
    category: { fr: 'Parcs et espaces verts', en: 'Parks and green spaces' },
    subcategory: { fr: 'Arbre endommagé', en: 'Damaged tree' },
    slaDays: 14,
    summary: {
      fr: 'Branche cassée suspendue au-dessus d’un trottoir après les vents de la semaine dernière.',
      en: 'Broken branch hanging over a sidewalk after last week’s winds.',
    },
    actions: [
      { fr: 'Périmètre de sécurité installé', en: 'Safety perimeter installed' },
      { fr: 'Évaluation par l’arboriculteur', en: 'Assessment by the arborist' },
      { fr: 'Élagage ajouté au calendrier de l’équipe', en: 'Pruning added to the crew schedule' },
    ],
    lateReasons: [
      {
        fr: 'Après la tempête, 60 requêtes d’arbres ont été ouvertes en trois jours : l’équipe traite d’abord les arbres menaçant des bâtiments.',
        en: 'After the storm, 60 tree requests were opened in three days: the crew is handling trees threatening buildings first.',
      },
      {
        fr: 'L’arbre est près d’une ligne électrique : l’élagage attend la présence d’Hydro-Québec sur place.',
        en: 'The tree is near a power line: pruning is waiting for Hydro-Québec to be on site.',
      },
    ],
  },
  {
    icon: 'water',
    category: { fr: 'Aqueduc', en: 'Water system' },
    subcategory: { fr: 'Fuite d’eau', en: 'Water leak' },
    slaDays: 2,
    summary: {
      fr: 'Écoulement d’eau continu signalé en bordure de rue, près d’une borne-fontaine.',
      en: 'Continuous water flow reported at the curb, near a fire hydrant.',
    },
    actions: [
      { fr: 'Équipe d’urgence dépêchée', en: 'Emergency crew dispatched' },
      { fr: 'Vanne fermée temporairement', en: 'Valve temporarily shut off' },
      { fr: 'Excavation planifiée', en: 'Excavation scheduled' },
    ],
    lateReasons: [
      {
        fr: 'La fuite provient d’une conduite principale : l’excavation exige une demande de localisation Info-Excavation, qui prend 72 heures.',
        en: 'The leak comes from a main pipe: excavation requires an Info-Excavation locate request, which takes 72 hours.',
      },
      {
        fr: 'La réparation a dû être reportée pour éviter de couper l’eau à une résidence pour aînés pendant la journée.',
        en: 'The repair had to be postponed to avoid cutting water to a seniors’ residence during the day.',
      },
    ],
  },
  {
    icon: 'sign',
    category: { fr: 'Signalisation', en: 'Signage' },
    subcategory: { fr: 'Panneau d’arrêt manquant', en: 'Missing stop sign' },
    slaDays: 3,
    summary: {
      fr: 'Panneau d’arrêt renversé à une intersection près d’une école, signalé par un brigadier scolaire.',
      en: 'Stop sign knocked down at an intersection near a school, reported by a crossing guard.',
    },
    actions: [
      { fr: 'Priorité haute attribuée (zone scolaire)', en: 'High priority set (school zone)' },
      { fr: 'Panneau temporaire posé', en: 'Temporary sign installed' },
      { fr: 'Remplacement permanent commandé', en: 'Permanent replacement ordered' },
    ],
    lateReasons: [
      {
        fr: 'Le socle en béton doit être refait avant de reposer le panneau permanent ; le panneau temporaire assure la sécurité en attendant.',
        en: 'The concrete base must be redone before the permanent sign goes back up; the temporary sign keeps the spot safe meanwhile.',
      },
      {
        fr: 'L’inventaire de panneaux d’arrêt était épuisé : la commande a été reçue hier et la pose est prévue demain.',
        en: 'The stop-sign stock was depleted: the order arrived yesterday and installation is scheduled tomorrow.',
      },
    ],
  },
]

const STREETS: Record<City, string[]> = {
  Brossard: [
    'boulevard de Rome',
    'boulevard Taschereau',
    'boulevard Lapinière',
    'boulevard Grande-Allée',
    'avenue Panama',
    'rue Lautrec',
    'boulevard Milan',
    'rue Bourassa',
  ],
  Windsor: [
    'rue St-Georges',
    'rue Georges-Guilbault',
    'rue Ambroise-Dearden',
    '3e Avenue',
    'rue Principale',
    'rue de l’Église',
  ],
} as Record<City, string[]>

function hashOf(s: string) {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/** Small deterministic PRNG so pins stay put between renders. */
function rng(seed: number) {
  let x = seed || 1
  return () => {
    x ^= x << 13
    x ^= x >>> 17
    x ^= x << 5
    return ((x >>> 0) % 10000) / 10000
  }
}

function insidePolygon([lat, lng]: Coord, poly: Coord[]) {
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [yi, xi] = poly[i]
    const [yj, xj] = poly[j]
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

function stateOf(days: number, sla: number): SlaState {
  if (days > sla) return 'late'
  if (days >= sla * 0.8) return 'risk'
  return 'ok'
}

function explanationFor(t: PinTemplate, state: SlaState, days: number, pick: number): Localized {
  if (state === 'late') return t.lateReasons[pick % t.lateReasons.length]
  if (state === 'risk') {
    const left = Math.max(0, t.slaDays - days)
    return {
      fr: `Le SLA est encore respecté, mais il reste ${left} jour${left > 1 ? 's' : ''} avant l’échéance. La dernière étape n’est pas encore planifiée : à surveiller.`,
      en: `The SLA is still met, but ${left} day${left > 1 ? 's' : ''} remain before the deadline. The last step isn’t scheduled yet: keep an eye on it.`,
    }
  }
  return {
    fr: 'Le traitement avance au rythme prévu : chaque étape a été franchie dans les délais habituels du service.',
    en: 'Handling is on pace: each step was completed within the department’s usual turnaround.',
  }
}

/** A flagged pin: target state, template index and (late only) the reason in the file. */
interface SpecialPin {
  state: SlaState
  tpl: number
  reason?: Localized
}

const NO_REASON: Localized = {
  fr: 'Aucune explication n’est consignée au dossier : la cause du retard reste à documenter auprès du service.',
  en: 'No explanation is recorded in the file: the cause of the delay still needs to be documented with the department.',
}

/**
 * Brossard: a few requests fall behind, at most 1 late and 3 at risk per
 * district. Matches FLAGGED_REQUESTS in data/requestDashboard.ts.
 * - B (districts 2-3): late pothole + 2 broken bins
 * - O (district 9): late streetlight + 1 broken bin
 * - I (district 9): 2 streetlights
 * - R (districts 7-8) and M (district 11): 1 broken bin each
 * Districts 1 and 4 stay all green.
 */
function specialPinsFor(city: City, sectorId: string): SpecialPin[] {
  if (city === 'Brossard') {
    const map: Record<string, SpecialPin[]> = {
      'br-b': [
        { state: 'late', tpl: 1, reason: RAIN_REASON },
        { state: 'risk', tpl: 2 },
        { state: 'risk', tpl: 2 },
      ],
      'br-o': [
        { state: 'late', tpl: 0, reason: NO_REASON },
        { state: 'risk', tpl: 2 },
      ],
      'br-i': [
        { state: 'risk', tpl: 0 },
        { state: 'risk', tpl: 0 },
      ],
      'br-r': [{ state: 'risk', tpl: 2 }],
      'br-m': [{ state: 'risk', tpl: 2 }],
    }
    return map[sectorId] ?? []
  }
  return sectorsFor(city)[0]?.id === sectorId
    ? [
        { state: 'late', tpl: 1, reason: RAIN_REASON },
        { state: 'risk', tpl: 2 },
        { state: 'risk', tpl: 2 },
      ]
    : []
}

const RAIN_REASON: Localized = {
  fr: 'Des pluies exceptionnelles ont détrempé la chaussée pendant plusieurs jours : l’enrobé ne peut pas être posé sur un sol gorgé d’eau, la réparation ne pouvait donc pas être faite dans les délais. Elle est planifiée dès le retour du temps sec.',
  en: 'Exceptional rainfall soaked the roadway for several days: asphalt can’t be laid on waterlogged ground, so the repair couldn’t be done on time. It’s scheduled as soon as dry weather returns.',
}

function daysFor(state: SlaState, sla: number, r: number): number {
  if (state === 'late') return sla + 4
  if (state === 'risk') return Math.min(sla, Math.max(1, Math.ceil(sla * 0.85)))
  // On time: well within the SLA (under 80 %).
  return Math.max(1, Math.min(Math.ceil(sla * 0.8) - 1, Math.round(sla * (0.2 + r * 0.5))))
}

function pinsForSector(sector: Sector, city: City): RequestPin[] {
  const h = hashOf(`${city}:${sector.id}`)
  const rand = rng(h)
  const lats = sector.polygon.map((p) => p[0])
  const lngs = sector.polygon.map((p) => p[1])
  const [minLat, maxLat] = [Math.min(...lats), Math.max(...lats)]
  const [minLng, maxLng] = [Math.min(...lngs), Math.max(...lngs)]
  // Fewer reports per sector (2 to 3).
  const specials = specialPinsFor(city, sector.id)
  const count = Math.max(2 + (h % 2), specials.length + 1)
  const streets = STREETS[city] ?? STREETS.Brossard
  const pins: RequestPin[] = []
  let tries = 0
  while (pins.length < count && tries < 200) {
    tries++
    const position: Coord = [
      minLat + rand() * (maxLat - minLat),
      minLng + rand() * (maxLng - minLng),
    ]
    if (!insidePolygon(position, sector.polygon)) continue
    const i = pins.length
    // Keep sla ≥ 3 for the at-risk pins so "close to deadline" is readable.
    let tpl = TEMPLATES[Math.floor(rand() * TEMPLATES.length)]
    const special = specials[i]
    const target: SlaState = special?.state ?? 'ok'
    // Flagged requests match the dashboard figures.
    if (special) tpl = TEMPLATES[special.tpl]
    const days = daysFor(target, tpl.slaDays, rand())
    const state = stateOf(days, tpl.slaDays)
    pins.push({
      id: `${sector.id}-${i}`,
      number: 20000 + (hashOf(`${sector.id}-${i}`) % 9000),
      sectorId: sector.id,
      position,
      icon: tpl.icon,
      category: tpl.category,
      subcategory: tpl.subcategory,
      address: `${100 + Math.floor(rand() * 8900)}, ${streets[Math.floor(rand() * streets.length)]}`,
      days,
      slaDays: tpl.slaDays,
      state,
      summary: tpl.summary,
      actions: tpl.actions,
      explanation:
        state === 'late' ? (special?.reason ?? NO_REASON) : explanationFor(tpl, state, days, i),
      status: statusFor(state, days, hashOf(`${sector.id}-${i}-s`)),
    })
  }
  return pins
}

/** The four Brossard demo requests (Charlotte Bouchard), placed at their real address. */
function demoPins(city: City): RequestPin[] {
  if (city !== 'Brossard') return []
  const sectors = sectorsFor(city)
  return BROSSARD_DEMO_REQUESTS.flatMap((r): RequestPin[] => {
    const sector = sectors.find((s) => insidePolygon(r.coords, s.polygon))
    if (!sector) return []
    const state = stateOf(r.daysOpen, r.slaDays)
    return [
      {
        id: `demo-${r.reqId}`,
        number: Number(r.reqId.replace(/\D/g, '')),
        sectorId: sector.id,
        position: r.coords,
        icon: r.pinIcon,
        category: r.category,
        subcategory: r.title,
        address: r.addressLines[0],
        days: r.daysOpen,
        slaDays: r.slaDays,
        state,
        summary: r.description,
        actions: r.history.map((h) => h.label),
        explanation: {
          fr: 'Requête reçue via ATNA : elle suit son cours dans les délais prévus par le SLA.',
          en: 'Request received via ATNA: it’s progressing within the SLA timeframe.',
        },
        status: r.reqId === 'REQ-2790' ? 'en_cours' : 'nouvelle',
      },
    ]
  })
}

/** Sample requests with their SLA state, placed inside each sector. */
export function requestPinsFor(city: City, sectorIds: string[]): RequestPin[] {
  const demo = demoPins(city).filter((p) => sectorIds.includes(p.sectorId))
  return [
    ...demo,
    ...sectorsFor(city)
      .filter((s) => sectorIds.includes(s.id))
      .flatMap((s) => pinsForSector(s, city)),
  ]
}
